// Whole-script semantic asset audit. Unlike per-episode extraction, this task
// sees the final global roster and only reports suspected duplicates/anomalies;
// it never mutates or merges the script by itself.

import {
  type AssetAuditAction,
  type AssetAuditConfidence,
  type AssetAuditIssue,
  type AssetAuditIssueType,
  type AssetAuditSnapshot,
} from "../../../domain/ingest/asset-audit.js";
import type { LlmTaskExecutor } from "../../types.js";
import { ASSET_AUDIT_SCHEMA } from "../schemas.js";

const ASSET_INPUT_COLUMNS = ["id", "name", "aliases", "importance", "refs", "episodes", "scenes", "format_key", "description", "evidence_refs"];
const STATE_INPUT_COLUMNS = ["address", "name", "refs", "episodes", "scenes", "frequency", "format_key", "description", "evidence_refs"];

export const ASSET_AUDIT_SYSTEM = [
  "你正在对一部已经完成解析的完整剧本进行全局资产一致性审计。目标是发现同一实体被重复建档、同一稳定视觉状态被重复拆分，以及值得人工重点复核的低频异常状态。",
  "输入是完整资产登记的紧凑行式快照，不是 JSON。第一行 ASSET_AUDIT_SNAPSHOT_V1 是协议版本；title|<剧名> 是剧名；A_COLUMNS/S_COLUMNS 是列定义，不是资产数据；[actors:N]、[locations:N]、[props:N] 分别开始人物、地点、道具分区，N 是该分区资产数并决定其中 A 行的资产类型。",
  `A 行表示一项资产。去掉开头的 A 后，各列从左到右固定为：${ASSET_INPUT_COLUMNS.join(" | ")}。id 是资产 ID；name 是规范名；aliases 是别名列表；importance 是重要度；refs/episodes/scenes 是引用次数、覆盖集数、覆盖场数；format_key 是仅消除格式噪声的名称键；description 是资产描述；evidence_refs 是少量实际引用位置。`,
  `S 行表示状态并隶属于同一分区中紧邻其上的 A 行。去掉开头的 S 后，各列从左到右固定为：${STATE_INPUT_COLUMNS.join(" | ")}。address 是 <asset_id>/<state_id> 形式的全局状态地址；name 是状态名；refs/episodes/scenes 含义同上；frequency 为 unused、singleton 或 -；description 是状态视觉描述；evidence_refs 是该状态的实际引用位置。`,
  "解析规则：只在未转义的 | 处分列，只在未转义的 ; 处分隔 aliases/evidence_refs 列表；- 表示空值或无频率标记；\\|、\\;、\\n、\\\\ 分别表示字段内容中的竖线、分号、换行和反斜杠。不要把 A_COLUMNS/S_COLUMNS 当作实体，也不要把 S 行跨资产归属。",
  "format_key 只折叠空白、标点、大小写、全半角等格式差异，是召回证据而不是同一实体的最终结论。",
  "",
  "一、人物身份一致性",
  "检查同一个人物是否因名称表达不同被登记成多个 actor。差异包括但不限于：空格、标点、大小写、全半角、简繁体；全名、简称、昵称、称谓、职位、旧名、化名、译名、音译；分词差异、轻微错别字、转录误差；名称附带身份、情绪、对白或状态说明；前期身份未明、后期又以真名重新登记。",
  "例如『林有粮』和『林 有粮』只是格式不同，但不能只按名称相似度合并。必须综合人物关系、剧情身份、外貌描述、行为轨迹、对白归属、出现位置和跨集连续性判断。姓名相似、身份相近或经常同框的不同人物绝不能误合。",
  "",
  "二、视觉状态一致性",
  "在同一个人物、地点或道具内部，检查是否因描述方式、颜色粒度、措辞详略或观察角度不同，把同一个稳定视觉状态拆成多个 state。重点关注同义/近义状态、同一套服装被重新命名、仅光线或局部细节造成的差异，以及状态名混入情绪、动作或场景说明。",
  "判断标准不是名称是否一致，而是是否真的需要分别制作独立、可复用的视觉参考资产。视觉主体和持续外观一致时建议归并；明确换装、变身、损毁、持续伤势或其他持续性变化则保持独立。",
  "",
  "三、低频与异常状态",
  "重点检查非 default 且未被引用、只在一个场景出现、出现后立即恢复原状态、与前后状态几乎相同或实际属于情绪/姿势/动作/光线/瞬时过程的状态。低频不等于错误：明确对应关键换装、剧情事件、持续伤势、变身或必要视觉变化时应保留，不要输出为问题。证据不足时 action=review。",
  "",
  "四、地点与道具一致性",
  "检查同一个物理地点或同一件具体道具是否因名称、前后缀、归属描述、简称、简繁体、转录差异或观察角度不同而重复登记。区分同一地点的不同名称与同一建筑中的不同独立空间，也区分同一件具体道具与同类但实际不同的多件道具。",
  "这里检查和建议合并的是重复 location 资产，不是 scene。相同地点可以承载多个不同剧情场次，不能因为地点相同就合并 scene。",
  "",
  "五、审计边界与输出",
  "只引用输入中真实存在的 asset/state id；只输出确实可疑、需要合并/删除/人工确认的项目，确认合理的资产或低频状态不要输出。不要直接修改剧本。",
  "每个问题必须给出具体证据和操作建议。高置信度仍只是建议；存在冲突或无法确认时 action=review、confidence=low 或 medium。",
  "调用 emit_asset_audit 工具返回审计结果。",
  "state_ids 必须填写输入里的完整状态 address（<asset_id>/<state_id>），不能只写可能在多个资产下重复的局部 state_id。",
  "没有问题时 issues 传空数组。",
].join("\n");

const ISSUE_TYPES = new Set<AssetAuditIssueType>([
  "duplicate_actor",
  "duplicate_location",
  "duplicate_prop",
  "duplicate_state",
  "suspicious_singleton_state",
  "unused_state",
  "needs_human_review",
]);
const ACTIONS = new Set<AssetAuditAction>(["merge", "delete", "review"]);
const CONFIDENCES = new Set<AssetAuditConfidence>(["high", "medium", "low"]);

// The roster can be large. This format declares column names once instead of
// repeating JSON keys for every asset/state, while preserving readable evidence.
function compactField(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "-";
  return trimmed
    .replace(/\\/g, "\\\\")
    .replace(/\r\n?|\n/g, "\\n")
    .replace(/\|/g, "\\|")
    .replace(/;/g, "\\;");
}

function compactList(values: string[]): string {
  return values.length > 0 ? values.map(compactField).join(";") : "-";
}

export function formatAssetAuditInput(snapshot: AssetAuditSnapshot): string {
  const lines = [
    "ASSET_AUDIT_SNAPSHOT_V1",
    `title|${compactField(snapshot.title)}`,
    `A_COLUMNS|${ASSET_INPUT_COLUMNS.join("|")}`,
    `S_COLUMNS|${STATE_INPUT_COLUMNS.join("|")}`,
  ];

  const appendSection = (name: "actors" | "locations" | "props", assets: AssetAuditSnapshot[typeof name]): void => {
    lines.push(`[${name}:${assets.length}]`);
    for (const asset of assets) {
      lines.push([
        "A",
        compactField(asset.id),
        compactField(asset.name),
        compactList(asset.aliases),
        compactField(asset.importance),
        String(asset.reference_count),
        String(asset.episode_count),
        String(asset.scene_count),
        compactField(asset.format_key),
        compactField(asset.description),
        compactList(asset.references),
      ].join("|"));
      for (const state of asset.states) {
        lines.push([
          "S",
          compactField(state.address),
          compactField(state.state_name),
          String(state.reference_count),
          String(state.episode_count),
          String(state.scene_count),
          state.frequency_flag ?? "-",
          compactField(state.format_key),
          compactField(state.description),
          compactList(state.references),
        ].join("|"));
      }
    }
  };

  appendSection("actors", snapshot.actors);
  appendSection("locations", snapshot.locations);
  appendSection("props", snapshot.props);
  return lines.join("\n");
}

const isObj = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const asArr = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const asStr = (value: unknown): string => typeof value === "string" ? value.trim() : "";
const strings = (value: unknown): string[] => [...new Set(asArr(value).map(asStr).filter(Boolean))];

export function parseAssetAuditInput(parsed: unknown, snapshot: AssetAuditSnapshot): AssetAuditIssue[] {
  if (!isObj(parsed)) throw new Error("asset audit response must be a JSON object");
  const validAssetIds = new Set([...snapshot.actors, ...snapshot.locations, ...snapshot.props].map((asset) => asset.id));
  const validStateIds = new Set([...snapshot.actors, ...snapshot.locations, ...snapshot.props].flatMap((asset) => asset.states.map((state) => state.address)));
  const issues: AssetAuditIssue[] = [];

  for (const rawIssue of asArr(parsed.issues)) {
    if (!isObj(rawIssue)) continue;
    const type = asStr(rawIssue.type) as AssetAuditIssueType;
    const action = asStr(rawIssue.action) as AssetAuditAction;
    const confidence = asStr(rawIssue.confidence) as AssetAuditConfidence;
    if (!ISSUE_TYPES.has(type) || !ACTIONS.has(action) || !CONFIDENCES.has(confidence)) continue;
    const assetIds = strings(rawIssue.asset_ids).filter((id) => validAssetIds.has(id));
    const stateIds = strings(rawIssue.state_ids).filter((id) => validStateIds.has(id));
    if (assetIds.length === 0 && stateIds.length === 0) continue;
    const survivor = asStr(rawIssue.suggested_survivor);
    const suggestedSurvivor = survivor && (validAssetIds.has(survivor) || validStateIds.has(survivor)) ? survivor : null;
    issues.push({
      type,
      asset_ids: assetIds,
      state_ids: stateIds,
      names: strings(rawIssue.names),
      evidence: asStr(rawIssue.evidence),
      recommendation: asStr(rawIssue.recommendation),
      suggested_survivor: suggestedSurvivor,
      action,
      confidence,
    });
  }
  return issues;
}

export async function runAssetAudit(executor: LlmTaskExecutor, snapshot: AssetAuditSnapshot, signal?: AbortSignal): Promise<AssetAuditIssue[]> {
  const response = await executor.generateTool("ingest.asset-audit", {
    system: ASSET_AUDIT_SYSTEM,
    user: formatAssetAuditInput(snapshot),
    tool: {
      name: "emit_asset_audit",
      description: "返回全剧资产一致性审计发现",
      inputSchema: ASSET_AUDIT_SCHEMA,
    },
    ...(signal ? { signal } : {}),
  });
  return parseAssetAuditInput(response.input, snapshot);
}
